import { useState, useCallback, useRef, useEffect } from "react";
import { ASSETS } from "../assetManifest.js";

interface UseAudioProps {
  musicVolume?: number;
  sfxVolume?: number;
}

const useAudio = ({ musicVolume = 0.5, sfxVolume = 0.8 }: UseAudioProps = {}) => {
  const [audioInitialized, setAudioInitialized] = useState(false);
  const musicRef = useRef<HTMLAudioElement | null>(null);
  const sfxCache = useRef<Map<string, HTMLAudioElement>>(new Map());
  const audioContextRef = useRef<AudioContext | null>(null);
  const userInteracted = useRef(false);

  // Initialize audio context only
  const initializeAudioContext = useCallback(async () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }

      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
    } catch (error) {
      console.log("Audio context initialization failed:", error);
    }
  }, []);

  // Initialize music after user interaction
  const initializeMusic = useCallback(async () => {
    if (!userInteracted.current || musicRef.current) return;

    try {
      await initializeAudioContext();

      if (ASSETS.music?.cafeAmbient) {
        musicRef.current = new Audio();
        musicRef.current.loop = true;
        musicRef.current.volume = musicVolume;
        
        // Add error handling for audio loading
        musicRef.current.addEventListener('error', (e) => {
          console.log("Music loading failed, continuing without background music:", e);
          musicRef.current = null;
        });

        musicRef.current.addEventListener('canplaythrough', async () => {
          try {
            if (musicRef.current && userInteracted.current) {
              await musicRef.current.play();
              setAudioInitialized(true);
            }
          } catch (error) {
            console.log("Music playback failed:", error);
          }
        });

        // Set source after event listeners are attached
        musicRef.current.src = ASSETS.music.cafeAmbient.path;
      } else {
        // No music asset available, mark as initialized anyway
        setAudioInitialized(true);
      }
    } catch (error) {
      console.log("Music initialization failed:", error);
      // Mark as initialized even if music fails, so SFX can still work
      setAudioInitialized(true);
    }
  }, [musicVolume, initializeAudioContext]);

  // Handle first user interaction
  const handleUserInteraction = useCallback(async () => {
    if (userInteracted.current) return;
    
    userInteracted.current = true;
    await initializeMusic();
  }, [initializeMusic]);

  // Set up global interaction listeners
  useEffect(() => {
    if (!userInteracted.current) {
      const handleInteraction = () => {
        handleUserInteraction();
        // Remove listeners after first interaction
        document.removeEventListener('click', handleInteraction);
        document.removeEventListener('touchstart', handleInteraction);
        document.removeEventListener('keydown', handleInteraction);
      };

      document.addEventListener('click', handleInteraction);
      document.addEventListener('touchstart', handleInteraction);
      document.addEventListener('keydown', handleInteraction);

      return () => {
        document.removeEventListener('click', handleInteraction);
        document.removeEventListener('touchstart', handleInteraction);
        document.removeEventListener('keydown', handleInteraction);
      };
    }
  }, [handleUserInteraction]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (musicRef.current) {
        musicRef.current.pause();
        musicRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  // Update music volume when musicVolume prop changes
  useEffect(() => {
    if (musicRef.current) {
      musicRef.current.volume = Math.max(0, Math.min(1, musicVolume));
    }
  }, [musicVolume]);

  // Update SFX volume for all cached sounds when sfxVolume prop changes
  useEffect(() => {
    sfxCache.current.forEach((audio) => {
      audio.volume = Math.max(0, Math.min(1, sfxVolume));
    });
  }, [sfxVolume]);

  const playSfx = useCallback(async (key: string) => {
    // Ensure user has interacted first
    if (!userInteracted.current) {
      await handleUserInteraction();
    }

    const sfxAsset = ASSETS.sfx?.[key];
    if (!sfxAsset) {
      console.log(`SFX asset ${key} not found`);
      return;
    }

    try {
      let audio = sfxCache.current.get(key);
      if (!audio) {
        audio = new Audio();
        audio.volume = Math.max(0, Math.min(1, sfxVolume));
        
        // Add error handling for SFX loading
        audio.addEventListener('error', (e) => {
          console.log(`SFX ${key} loading failed:`, e);
          sfxCache.current.delete(key);
        });

        audio.src = sfxAsset.path;
        sfxCache.current.set(key, audio);
      }

      audio.volume = Math.max(0, Math.min(1, sfxVolume));
      audio.currentTime = 0;
      
      await audio.play();
    } catch (error) {
      console.log(`Failed to play SFX ${key}:`, error);
      // Remove failed audio from cache so it can be retried
      sfxCache.current.delete(key);
    }
  }, [sfxVolume, handleUserInteraction]);

  return {
    playSfx,
    musicVolume,
    sfxVolume,
    audioInitialized,
  };
};

export default useAudio;
